import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  Tooltip,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { toast } from 'utils/toast';
import codiconTtfUrl from '../../../assets/fonts/codicon.ttf';

export interface FullScreenJsonEditorModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  initialValue: string;
  onSave: (value: string) => void;
}

const isValidJson = (str: string): boolean => {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

const formatJson = (str: string): string => {
  try {
    const parsed = JSON.parse(str);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return str;
  }
};

export const FullScreenJsonEditorModal: React.FC<FullScreenJsonEditorModalProps> = ({
  open,
  onClose,
  title,
  initialValue,
  onSave
}) => {
  const theme = useTheme();
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const [content, setContent] = useState(initialValue);

  // Force Monaco Find widget to use our codicon font (Monaco's @font-face path often 404s in CRA).
  useEffect(() => {
    const id = 'codicon-override-styles';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = `.codicon[class*="codicon-"]{font-family:codicon-fixed !important;font:normal normal normal 16px/1 codicon-fixed !important;}`;
      document.head.appendChild(style);
    }
    if (typeof document.fonts?.check !== 'function' || document.fonts.check('1em codicon-fixed')) return;
    const font = new FontFace('codicon-fixed', `url(${codiconTtfUrl}) format("truetype")`, { display: 'block' });
    font.load().then((loaded) => { document.fonts.add(loaded); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) {
      setContent(initialValue);
    }
  }, [open, initialValue]);

  const handleEditorMount = (editor: editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;
  };

  const handleFormat = () => {
    const toFormat = editorRef.current?.getValue() ?? content;
    const formatted = formatJson(toFormat);
    if (formatted !== toFormat) {
      setContent(formatted);
    }
  };

  const handleApply = () => {
    const value = editorRef.current?.getValue() ?? content;
    const trimmed = value.trim();
    if (!isValidJson(trimmed)) {
      toast.error('Invalid JSON. Fix errors before applying.');
      return;
    }
    onSave(trimmed);
    onClose();
  };

  const handleClose = () => {
    editorRef.current = null;
    onClose();
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '90vw',
          height: '90vh',
          maxWidth: '90vw',
          maxHeight: '90vh',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          px: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.text.primary,
          fontSize: '1.25rem',
          fontWeight: 600
        }}
      >
        {title}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '& .MuiIconButton-root': {
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.action.hover, 0.5),
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main
              }
            }
          }}
        >
          <Tooltip title="Format JSON">
            <IconButton size="small" onClick={handleFormat} sx={{ p: 1 }}>
              <FormatAlignLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={handleClose} sx={{ p: 1 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'visible', flex: 1, minHeight: 0, position: 'relative' }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'visible', position: 'relative' }}>
          <Editor
            height="100%"
            language="json"
            value={content}
            onChange={(val) => setContent(val ?? '')}
            onMount={handleEditorMount}
            theme={isDark ? 'vs-dark' : 'light'}
            options={{
              minimap: { enabled: true },
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              formatOnPaste: true,
              formatOnType: true,
              tabSize: 2,
              automaticLayout: true
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          gap: 2
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5 }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{ textTransform: 'none', minWidth: 100 }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
